import { Component } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { ImgDown } from './Images/ImgDown';
import { ImgTimes } from './Images/ImgTimes';
import { ImgChevronLeft } from './Images/ImgChevronLeft';
import { ImgChevronRight } from './Images/ImgChevronRight';

interface ImageFullscreenProps {
  images: string[];
  startIndex: number;
  onClose: () => void;
}

function isTouchDevice() {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
}

@observer
export default class ImageFullscreen extends Component<ImageFullscreenProps> {
  @observable isLoadingFullscreen = false;
  @observable zoomScale = 1;
  @observable translateX = 0;
  @observable translateY = 0;
  @observable currentImageIndex = 0;
  @observable isTransitioningImage = false;
  @observable showNavButtons = false;
  @observable isDownloading = false;
  @observable nextImageSrc: string | null = null;

  private preloadCache = new Map<string, HTMLImageElement>();

  private initialDistance = 0;
  private initialScale = 1;
  private imageRect: DOMRect | null = null;
  private isPinching = false;
  private isTransitioning = false;
  private transformOrigin = 'center center';
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;

  constructor(props: ImageFullscreenProps) {
    super(props);
    makeObservable(this);
    this.currentImageIndex = Math.max(0, Math.min(props.startIndex, props.images.length - 1));
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'hidden';
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'unset';
  }

  @action
  closeFullscreen = () => {
    this.props.onClose();
  };

  @action
  setLoadingFullscreen = (loading: boolean) => {
    this.isLoadingFullscreen = loading;
  };

  @action
  setZoom = (scale: number, x: number = 0, y: number = 0) => {
    this.zoomScale = scale;
    this.translateX = x;
    this.translateY = y;
  };

  @action
  resetZoom = () => {
    this.isTransitioning = true;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  centerZoom = () => {
    this.isTransitioning = true;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  navigateImage = (direction: 'prev' | 'next') => {
    const { images } = this.props;
    if (!images || images.length <= 1) return;

    // Reset zoom immediately without transition
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isTransitioning = false;

    const nextIndex = direction === 'next'
      ? (this.currentImageIndex + 1) % images.length
      : (this.currentImageIndex - 1 + images.length) % images.length;

    const nextSrc = images[nextIndex];

    const cachedImg = this.getPreloadedImage(nextSrc);
    if (cachedImg) {
      this.setLoadingFullscreen(false);
      this.isTransitioningImage = true;
      setTimeout(() => {
        this.currentImageIndex = nextIndex;
        this.isTransitioningImage = false;
        requestAnimationFrame(() => {
          this.nextImageSrc = null;
        });
      }, 300);
    } else {
      this.setLoadingFullscreen(true);
      const img = new Image();
      img.onload = () => {
        this.preloadCache.set(nextSrc, img);
        this.setLoadingFullscreen(false);
        this.isTransitioningImage = true;
        setTimeout(() => {
          this.currentImageIndex = nextIndex;
          this.isTransitioningImage = false;
          requestAnimationFrame(() => {
            this.nextImageSrc = null;
          });
        }, 300);
      };
      img.onerror = () => {
        this.setLoadingFullscreen(false);
        this.isTransitioningImage = true;
        setTimeout(() => {
          this.currentImageIndex = nextIndex;
          this.isTransitioningImage = false;
          requestAnimationFrame(() => {
            this.nextImageSrc = null;
          });
        }, 300);
      };
      img.src = nextSrc;
    }
  };

  @action
  setShowNavButtons = (show: boolean) => {
    this.showNavButtons = show;
  };

  private preloadNextImage = () => {
    const { images } = this.props;
    if (!images || images.length <= 1) return;

    const nextIndex = (this.currentImageIndex + 1) % images.length;
    const nextSrc = images[nextIndex];

    if (this.preloadCache.has(nextSrc)) return;

    const img = new Image();
    img.onload = () => {
      this.preloadCache.set(nextSrc, img);
      requestAnimationFrame(() => {
        const followingIndex = (nextIndex + 1) % images.length;
        const followingSrc = images[followingIndex];
        if (!this.preloadCache.has(followingSrc)) {
          const img2 = new Image();
          img2.src = followingSrc;
          img2.onload = () => {
            this.preloadCache.set(followingSrc, img2);
          };
        }
      });
    };
    img.src = nextSrc;
  };

  private getPreloadedImage = (src: string): HTMLImageElement | undefined => {
    return this.preloadCache.get(src);
  };

  @action
  setDownloading = (downloading: boolean) => {
    this.isDownloading = downloading;
  };

  handleFullscreenImageLoad = () => {
    this.setLoadingFullscreen(false);
    this.preloadNextImage();
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.closeFullscreen();
    } else if (e.key === 'ArrowLeft') {
      this.navigateImage('prev');
    } else if (e.key === 'ArrowRight') {
      this.navigateImage('next');
    }
  };

  getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.isSwiping = false;
      this.initialDistance = this.getTouchDistance(e.touches);
      this.initialScale = this.zoomScale;

      this.imageRect = e.currentTarget.getBoundingClientRect();
      const touchCenter = this.getTouchCenter(e.touches);

      const originX = touchCenter.x - this.imageRect.left;
      const originY = touchCenter.y - this.imageRect.top;
      this.transformOrigin = `${originX}px ${originY}px`;
    } else if (e.touches.length === 1) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = Date.now();
      this.isSwiping = true;
    }
  };

  handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;

    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = this.getTouchDistance(e.touches);
      let scale = (currentDistance / this.initialDistance) * this.initialScale;
      scale = Math.max(1, Math.min(scale, 5));
      this.setZoom(scale);
    } else if (this.isSwiping && e.touches.length === 1) {
      const deltaX = Math.abs(e.touches[0].clientX - this.touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);

      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  handleTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isPinching) {
      this.isPinching = false;
      this.resetZoom();
      setTimeout(() => {
        this.transformOrigin = 'center center';
      }, 300);
    } else if (this.isSwiping) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndTime = Date.now();

      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;
      const deltaTime = touchEndTime - this.touchStartTime;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / deltaTime;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && velocity > 0.3) {
        const { images } = this.props;
        if (images && images.length > 1) {
          if (deltaX > 0) {
            this.navigateImage('prev');
          } else {
            this.navigateImage('next');
          }
        }
      }

      this.isSwiping = false;
    }
  };

  handleImageClick = () => {
    const { images } = this.props;
    if (images && images.length > 1) {
      this.navigateImage('next');
    }
  };

  @action.bound
  downloadImage = async () => {
    const { images } = this.props;
    let imageUrl = images && images.length > 0 ? images[this.currentImageIndex] : '';

    this.setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Network response is ${response.status}: ` + response.statusText);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `image-${Date.now()}.jpg`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      this.setDownloading(false);
    }
  };

  getCurrentImageSrc = () => {
    const { images } = this.props;
    return images && images.length > 0 ? images[this.currentImageIndex] : '';
  };

  render() {
    const { images } = this.props;
    const hasGallery = images && images.length > 1;

    return (
      <div
        className="fullscreen-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            this.closeFullscreen();
          }
        }}
        onMouseMove={() => {
          if (hasGallery) this.setShowNavButtons(true);
        }}
        onMouseLeave={() => {
          if (hasGallery) this.setShowNavButtons(false);
        }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.3s ease'
        }}
      >
        {/* Controls Bar */}
        <div
          className="fullscreen-controls"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 2001
          }}
        >
          <button
            onClick={this.downloadImage}
            className="control-button"
            title="Download Image"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              color: 'white',
              cursor: this.isDownloading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              fontSize: '18px',
              width: '48px',
              height: '48px',
              opacity: this.isDownloading ? 0.6 : 1,
              position: 'relative'
            }}
          >
            {this.isDownloading ? (
              <span
                style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid color-mix(in srgb, var(--text-primary) 20%, transparent)',
                  borderTop: '2px solid var(--text-primary)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}
              />
            ) : (
              <ImgDown size={20} />
            )}
          </button>
          <button
            onClick={this.closeFullscreen}
            className="control-button"
            title="Close (ESC)"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              fontSize: '20px',
              width: '48px',
              height: '48px'
            }}
          >
            <ImgTimes size={20} />
          </button>
        </div>

        {/* Gallery Counter */}
        {hasGallery && (
          <div
            style={{
              position: 'fixed',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              zIndex: 2001,
              backdropFilter: 'blur(10px)'
            }}
          >
            {this.currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Navigation Buttons */}
        {hasGallery && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                this.navigateImage('prev');
              }}
              style={{
                position: 'fixed',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                zIndex: 2001,
                opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                transition: 'all 0.3s ease',
                pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
              }}
            >
              <ImgChevronLeft size={24} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                this.navigateImage('next');
              }}
              style={{
                position: 'fixed',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                zIndex: 2001,
                opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                transition: 'all 0.3s ease',
                pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
              }}
            >
              <ImgChevronRight size={24} />
            </button>
          </>
        )}

        {/* Full Size Image Container with Cross-fade */}
        <div
          className="fullscreen-image-container"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            position: 'relative'
          }}
        >
          {this.isLoadingFullscreen && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2002,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid color-mix(in srgb, var(--text-primary) 20%, transparent)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
          )}

          {this.nextImageSrc && (
            <img
              src={this.nextImageSrc}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: '100vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                opacity: this.isTransitioningImage ? 1 : 0,
                transition: 'opacity 0.3s ease',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                cursor: hasGallery ? 'pointer' : 'default'
              }}
            />
          )}

          <img
            src={this.getCurrentImageSrc()}
            alt=""
            onLoad={this.handleFullscreenImageLoad}
            style={{
              maxWidth: '100%',
              maxHeight: '100vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              transform: `scale(${this.zoomScale})`,
              transformOrigin: this.transformOrigin,
              opacity: this.isTransitioningImage ? 0 : 1,
              transition: this.isTransitioningImage ? 'opacity 0.3s ease' : undefined,
              position: 'relative',
              zIndex: 2,
              cursor: hasGallery ? 'pointer' : 'default'
            }}
            onClick={this.handleImageClick}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
          />
        </div>

        {/* Instructions */}
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            opacity: 0.7,
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 2
          }}
        >
          {!isTouchDevice() ? (
            hasGallery
              ? 'Click image, use arrow keys or buttons to navigate • Press ESC to close'
              : 'Tap outside image or press ESC to close'
          ) : (
            hasGallery
              ? 'Tap image or swipe left/right to navigate • Tap outside to close'
              : 'Tap outside image to close'
          )}
        </div>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
}
